/**
 * Cross-domain relationship graph.
 *
 * Life OS is modelled as objects + typed relationships, not pages. A task, a
 * project, a problem, a goal, a note, a person — any canonical entity — can be
 * linked to any other with a typed, directed relationship. This is the layer
 * behind "one object, many lenses", backlinks, and "used in" rails: the visible
 * IA stays calm and hierarchical while the underlying model is a graph.
 *
 * Everything here is PURE. Relationships are stored as plain edges; the index,
 * neighbour/backlink queries, cycle checks, and inverse lookups are all
 * deterministic functions over that edge set.
 */

import type { EntityId } from './ids.js';
import type { EntityKind } from './records.js';

/** A pointer to any canonical entity. */
export interface EntityRef {
  readonly kind: EntityKind;
  readonly id: EntityId;
}

/**
 * Curated relationship vocabulary (directed). Each kind has a human-readable
 * inverse label so a backlink can be shown from the target's point of view.
 * `acyclic` kinds form hierarchies/dependencies and must never create a cycle.
 */
export type RelationKind =
  | 'relates_to'
  | 'blocks'
  | 'affects'
  | 'caused_by'
  | 'motivates'
  | 'supports'
  | 'contains'
  | 'part_of'
  | 'mitigates'
  | 'requires'
  | 'evidence_for'
  | 'derived_from'
  | 'scheduled_in';

export const RELATION_KINDS: readonly RelationKind[] = [
  'relates_to', 'blocks', 'affects', 'caused_by', 'motivates', 'supports',
  'contains', 'part_of', 'mitigates', 'requires', 'evidence_for',
  'derived_from', 'scheduled_in',
] as const;

interface RelationMeta {
  readonly inverseLabel: string;
  readonly acyclic: boolean;
  readonly symmetric: boolean;
}

export const RELATION_META: Readonly<Record<RelationKind, RelationMeta>> = {
  relates_to:   { inverseLabel: 'related to', acyclic: false, symmetric: true },
  blocks:       { inverseLabel: 'blocked by', acyclic: false, symmetric: false },
  affects:      { inverseLabel: 'affected by', acyclic: false, symmetric: false },
  caused_by:    { inverseLabel: 'causes', acyclic: true, symmetric: false },
  motivates:    { inverseLabel: 'motivated by', acyclic: false, symmetric: false },
  supports:     { inverseLabel: 'supported by', acyclic: false, symmetric: false },
  contains:     { inverseLabel: 'part of', acyclic: true, symmetric: false },
  part_of:      { inverseLabel: 'contains', acyclic: true, symmetric: false },
  mitigates:    { inverseLabel: 'mitigated by', acyclic: false, symmetric: false },
  requires:     { inverseLabel: 'required by', acyclic: true, symmetric: false },
  evidence_for: { inverseLabel: 'has evidence', acyclic: false, symmetric: false },
  derived_from: { inverseLabel: 'source of', acyclic: true, symmetric: false },
  scheduled_in: { inverseLabel: 'holds', acyclic: false, symmetric: false },
};

export interface Relationship {
  readonly id: string;
  readonly from: EntityRef;
  readonly to: EntityRef;
  readonly kind: RelationKind;
  readonly createdAt: string;
  readonly note: string | null;
}

/** Stable string key for an entity ref. */
export function refKey(ref: EntityRef): string {
  return `${ref.kind}:${String(ref.id)}`;
}

export function sameRef(a: EntityRef, b: EntityRef): boolean {
  return a.kind === b.kind && String(a.id) === String(b.id);
}

/** Deterministic edge id — links are deduped by (from, kind, to). */
export function relationshipId(from: EntityRef, kind: RelationKind, to: EntityRef): string {
  return `${refKey(from)}|${kind}|${refKey(to)}`;
}

export interface GraphIndex {
  readonly out: ReadonlyMap<string, readonly Relationship[]>;
  readonly in: ReadonlyMap<string, readonly Relationship[]>;
}

/** Build directed adjacency (outgoing + incoming) keyed by ref. Pure. */
export function buildGraphIndex(relationships: readonly Relationship[]): GraphIndex {
  const out = new Map<string, Relationship[]>();
  const inc = new Map<string, Relationship[]>();
  for (const r of relationships) {
    (out.get(refKey(r.from)) ?? out.set(refKey(r.from), []).get(refKey(r.from))!).push(r);
    (inc.get(refKey(r.to)) ?? inc.set(refKey(r.to), []).get(refKey(r.to))!).push(r);
  }
  return { out, in: inc };
}

/** Outgoing edges from a ref. */
export function outgoing(index: GraphIndex, ref: EntityRef): readonly Relationship[] {
  return index.out.get(refKey(ref)) ?? [];
}

/** Incoming edges to a ref (its backlinks). */
export function backlinks(index: GraphIndex, ref: EntityRef): readonly Relationship[] {
  return index.in.get(refKey(ref)) ?? [];
}

/** All distinct entities directly connected to a ref, in either direction. */
export function neighbors(index: GraphIndex, ref: EntityRef): readonly EntityRef[] {
  const seen = new Map<string, EntityRef>();
  for (const r of outgoing(index, ref)) seen.set(refKey(r.to), r.to);
  for (const r of backlinks(index, ref)) seen.set(refKey(r.from), r.from);
  seen.delete(refKey(ref));
  return [...seen.values()];
}

/**
 * Would adding `from -[kind]-> to` create a cycle among edges of the same
 * acyclic kind? Returns false for non-acyclic kinds. Pure.
 */
export function wouldCreateCycle(
  relationships: readonly Relationship[],
  from: EntityRef,
  to: EntityRef,
  kind: RelationKind,
): boolean {
  if (!RELATION_META[kind].acyclic) return false;
  if (sameRef(from, to)) return true;
  // Can we already reach `from` starting at `to` following same-kind edges?
  const adj = new Map<string, EntityRef[]>();
  for (const r of relationships) {
    if (r.kind !== kind) continue;
    const k = refKey(r.from);
    (adj.get(k) ?? adj.set(k, []).get(k)!).push(r.to);
  }
  const target = refKey(from);
  const stack = [to];
  const visited = new Set<string>();
  while (stack.length) {
    const cur = stack.pop()!;
    const ck = refKey(cur);
    if (ck === target) return true;
    if (visited.has(ck)) continue;
    visited.add(ck);
    for (const nxt of adj.get(ck) ?? []) stack.push(nxt);
  }
  return false;
}

/** Human-readable label for how `ref` relates via an edge, from ref's view. */
export function edgeLabelFrom(ref: EntityRef, r: Relationship): string {
  if (sameRef(r.from, ref)) return r.kind.replace(/_/g, ' ');
  return RELATION_META[r.kind].inverseLabel;
}
